(() => {
  'use strict';
  if (customElements.get('retailer-bulk-order')) return;
  const core = window.RetailerBulkCore;
  const csv = window.RetailerCSV;
  let cartMutationBusy = false;
  const productPath = /^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})?\/)?products\/[^/]+\/?$/i;
  const catalogPath = /^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})?\/)?collections\/all\/?$/i;
  const cartPath = /^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})?\/)?cart(?:\.js|\/add\.js)$/i;
  const validId = (value) => typeof value === 'string' && /^[1-9][0-9]{0,19}$/.test(value);
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  class RetailerBulkOrder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.updateWorkingState();
        return;
      }
      this.initialized = true;
      this.catalog = null;
      this.rows = [];
      this.cartCounts = new Map();
      this.limit = 20;
      this.catalogRequests = new Set();
      this.search = this.querySelector('[data-quick-search]');
      this.searchStatus = this.querySelector('[data-search-status]');
      this.csvStatus = this.querySelector('[data-csv-status]');
      this.orderStatus = this.querySelector('[data-order-status]');
      this.file = this.querySelector('[data-csv-file]');
      this.dropzone = this.querySelector('[data-csv-drop]');
      this.addCSV = this.querySelector('[data-add-csv]');
      this.stopButton = this.querySelector('[data-stop]');
      this.more = this.querySelector('[data-show-more]');
      this.workingNotice = this.querySelector('[data-working-notice]');
      this.reportButtons = Array.from(this.querySelectorAll('[data-download-report]'));
      this.warnBeforeLeaving = (event) => {
        event.preventDefault();
        event.returnValue = '';
      };
      this.reportButtons.forEach((button) =>
        button.addEventListener('click', () =>
          this.downloadReport(button.dataset.downloadReport === 'attention'),
        ),
      );
      this.file.disabled = false;
      this.dropzone.disabled = false;
      this.querySelector('[data-search-button]').disabled = false;
      this.search.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.runSearch(), 180);
      });
      this.querySelector('[data-quick-form]').addEventListener('submit', (event) => {
        event.preventDefault();
        clearTimeout(this.timer);
        this.runSearch();
      });
      this.more.addEventListener('click', () => {
        this.limit += 20;
        this.renderResults();
      });
      this.file.addEventListener('change', () => this.reviewFiles(this.file.files));
      this.dropzone.addEventListener('click', () => {
        if (!this.busy && !this.locked) this.file.click();
      });
      let dragDepth = 0;
      const hasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');
      this.dropzone.addEventListener('dragenter', (event) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        dragDepth++;
        if (!this.busy && !this.locked) this.dropzone.classList.add('is-dragging');
      });
      this.dropzone.addEventListener('dragover', (event) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = this.busy || this.locked ? 'none' : 'copy';
      });
      this.dropzone.addEventListener('dragleave', (event) => {
        event.preventDefault();
        dragDepth = Math.max(0, dragDepth - 1);
        if (!dragDepth) this.dropzone.classList.remove('is-dragging');
      });
      this.dropzone.addEventListener('drop', (event) => {
        event.preventDefault();
        dragDepth = 0;
        this.dropzone.classList.remove('is-dragging');
        if (!this.busy && !this.locked) this.reviewFiles(event.dataTransfer?.files || []);
      });
      this.addCSV.addEventListener('click', () => this.addUpload());
      this.stopButton.addEventListener('click', () => {
        this.stopRequested = true;
        this.stopButton.disabled = true;
      });
      this.querySelector('[data-sample]').addEventListener('click', () => {
        // A code in the first column may be a SKU or a barcode.
        csv.download(
          csv.encode([
            ['sku', 'quantity'],
            ['60157', 6],
            ['762486601576', 1],
          ]),
          'bulk-order-sample.csv',
        );
      });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.stopRequested = true;
      this.searchSequence = (this.searchSequence || 0) + 1;
      window.removeEventListener('beforeunload', this.warnBeforeLeaving);
      // Cancel browsing work, but let an in-flight cart write finish confirmation.
      this.catalogRequests.forEach((controller) => controller.abort());
    }

    updateWorkingState() {
      const working = !!(this.busy || this.activeSearches);
      this.workingNotice.hidden = !working;
      if (working && this.isConnected) window.addEventListener('beforeunload', this.warnBeforeLeaving);
      else window.removeEventListener('beforeunload', this.warnBeforeLeaving);
    }
    updateReports() {
      for (const button of this.reportButtons) {
        button.hidden = !core.orderReport(this.rows, button.dataset.downloadReport === 'attention').length;
        button.disabled = !!this.busy;
      }
    }
    downloadReport(attentionOnly) {
      if (this.busy || !core.orderReport(this.rows, attentionOnly).length) return;
      const filename = `bulk-order-${attentionOnly ? 'needs-attention' : 'report'}-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
      csv.download(core.reportCSV(this.rows, attentionOnly), filename);
    }
    url(path, kind) {
      if (typeof path !== 'string' || path.length > 4096 || /[\\\u0000-\u0020\u007f]/u.test(path))
        throw new Error('Invalid store address.');
      const url = new URL(path, location.origin);
      if (
        url.origin !== location.origin ||
        url.username ||
        url.password ||
        url.hash ||
        !['http:', 'https:'].includes(url.protocol) ||
        /%(?:2f|5c|00|0a|0d)/i.test(url.pathname)
      )
        throw new Error('Invalid store address.');
      if (
        (kind === 'product' && !productPath.test(url.pathname)) ||
        (kind === 'catalog' && !catalogPath.test(url.pathname))
      )
        throw new Error('Unexpected catalog address.');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview && /^[0-9]{1,20}$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
      return url;
    }
    async request(url, options = {}) {
      url = this.url(String(url));
      const method = options.method || 'GET';
      const isData =
        url.searchParams.get('section_id') === 'retailer-bulk-data' &&
        (productPath.test(url.pathname) || catalogPath.test(url.pathname));
      const isDisplay =
        url.pathname === location.pathname &&
        url.searchParams.get('sections') === 'cart-icon-bubble,cart-drawer';
      if (
        (method !== 'GET' && method !== 'POST') ||
        (method === 'POST' && (!cartPath.test(url.pathname) || !url.pathname.endsWith('/add.js'))) ||
        (method === 'GET' &&
          !(isData || isDisplay || (cartPath.test(url.pathname) && url.pathname.endsWith('/cart.js'))))
      )
        throw new Error('Unexpected store request.');
      if (method === 'POST') {
        const payload = JSON.parse(options.body);
        if (
          !Array.isArray(payload.items) ||
          payload.items.length < 1 || payload.items.length > core.limits.batchSize ||
          payload.items.some((item) => !item || !validId(item.id) || !core.validQuantity(item.quantity)) ||
          new Set(payload.items.map((item) => item.id)).size !== payload.items.length
        )
          throw new Error('Invalid cart addition.');
        // Only variant ID and quantity ever reach Shopify. Prices and discounts cannot be supplied by the CSV.
        options = {
          ...options,
          body: JSON.stringify({ items: payload.items.map(({ id, quantity }) => ({ id, quantity })) }),
        };
      }
      // Coalesce work at the callers and pace requests to avoid accidental request bursts.
      const turn = this.requestTurn || Promise.resolve();
      let release;
      this.requestTurn = new Promise((resolve) => {
        release = resolve;
      });
      await turn;
      const delay = Math.max(0, (this.nextRequestAt || 0) - Date.now());
      if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
      this.nextRequestAt = Date.now() + 200;
      const controller = new AbortController(),
        timer = setTimeout(() => controller.abort(), 30000);
      if (isData) this.catalogRequests.add(controller);
      try {
        if (isData && !this.isConnected) throw new Error('Page closed while loading products.');
        if (method === 'POST' && !this.isConnected) throw new Error('Page closed before adding.');
        const response = await fetch(url, {
          ...options,
          headers: { ...options.headers, Accept: isData ? 'text/html' : 'application/json' },
          credentials: 'same-origin',
          mode: 'same-origin',
          redirect: 'error',
          cache: 'no-store',
          signal: controller.signal,
        });
        const contentType = (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
        // A .js cart endpoint can label JSON as JavaScript. Parse it strictly as data; never execute it.
        const acceptedTypes = isData
          ? ['text/html', 'application/xhtml+xml']
          : [
              'application/json',
              'text/json',
              'application/javascript',
              'text/javascript',
              'application/x-javascript',
              'text/plain',
            ];
        if (response.ok && contentType && !acceptedTypes.includes(contentType))
          throw new Error(
            isData
              ? 'The store returned an unexpected catalog response. Please refresh and try again.'
              : 'The store did not return cart data. Please refresh the store or theme preview and try again.',
          );
        const maximum = 8 * 1024 * 1024;
        if (Number(response.headers.get('content-length')) > maximum)
          throw new Error('Store response is too large.');
        let body = '';
        if (!response.body) throw new Error('Store response is empty.');
        const reader = response.body.getReader(),
          decoder = new TextDecoder('utf-8', { fatal: true });
        let bytes = 0;
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            bytes += value.byteLength;
            if (bytes > maximum) throw new Error('Store response is too large.');
            body += decoder.decode(value, { stream: true });
          }
          body += decoder.decode();
        } finally {
          await reader.cancel().catch(() => {});
          reader.releaseLock();
        }
        let json;
        if (!isData) {
          try {
            json = JSON.parse(body);
          } catch (error) {
            throw new Error('The store returned invalid cart data. Please refresh and try again.');
          }
          if (!json || typeof json !== 'object' || Array.isArray(json))
            throw new Error('The store returned invalid cart data. Please refresh and try again.');
        }
        return {
          ok: response.ok,
          status: response.status,
          text: async () => body,
          json: async () => (isData ? JSON.parse(body) : json),
        };
      } finally {
        clearTimeout(timer);
        controller.abort();
        this.catalogRequests.delete(controller);
        release();
      }
    }
    async dataPage(path, page) {
      if (!Number.isInteger(page) || page < 1 || page > 100) throw new Error('Invalid catalog page.');
      const url = this.url(path);
      if (!productPath.test(url.pathname) && !catalogPath.test(url.pathname))
        throw new Error('Unexpected catalog address.');
      url.search = '';
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview && /^[0-9]{1,20}$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('section_id', 'retailer-bulk-data');
      url.searchParams.set('page', page);
      url.searchParams.set('sort_by', 'title-ascending');
      url.searchParams.set('_bulk', `${Date.now()}-${Math.random()}`);
      const response = await this.request(url);
      if (!response.ok) throw new Error('Catalog could not be loaded. Please try again.');
      const template = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('template[data-bulk-catalog]');
      if (!template)
        throw new Error('Bulk Ordering data is unavailable. Check that the new theme is active.');
      const data = JSON.parse(template.content.textContent);
      if (
        !data ||
        typeof data !== 'object' ||
        data.error ||
        data.page !== page ||
        !Number.isInteger(data.pages) ||
        data.pages < 0 ||
        data.pages > 100 ||
        !Number.isInteger(data.total) ||
        data.total < 0 ||
        data.total > 25000 ||
        (data.total > 0 && (data.pages < page || data.total < data.pages))
      )
        throw new Error('The full catalog could not be read. Please try again.');
      return data;
    }
    validateRows(rows) {
      if (!Array.isArray(rows) || rows.length > 250) throw new Error('Incomplete catalog data.');
      for (const row of rows) {
        if (
          !row ||
          !validId(row.id) ||
          !['sku', 'barcode', 'name', 'url', 'image', 'price'].every(
            (key) => typeof row[key] === 'string' && row[key].length <= 4096,
          ) ||
          core.invalidCode(row.sku) ||
          core.invalidCode(row.barcode) ||
          typeof row.available !== 'boolean' ||
          typeof row.requires_plan !== 'boolean' ||
          (row.stock !== null && !Number.isSafeInteger(row.stock)) ||
          !Number.isSafeInteger(row.min) ||
          row.min < 1 ||
          !Number.isSafeInteger(row.increment) ||
          row.increment < 1 ||
          (row.max !== null && (!Number.isSafeInteger(row.max) || row.max < 1))
        )
          throw new Error('Incomplete variant details.');
        this.url(row.url, 'product');
      }
      return rows;
    }
    async productRows(path) {
      this.url(path, 'product');
      let pages = 1,
        total,
        id;
      const rows = [];
      for (let page = 1; page <= pages; page++) {
        const data = await this.dataPage(path, page);
        if (
          data.kind !== 'variants' ||
          !validId(data.id) ||
          (page > 1 && (data.total !== total || data.pages !== pages || data.id !== id))
        )
          throw new Error('Product changed while loading. Please try again.');
        pages = data.pages;
        total = data.total;
        id = data.id;
        rows.push(...this.validateRows(data.rows));
      }
      if (rows.length !== total || new Set(rows.map((row) => row.id)).size !== total)
        throw new Error('Not all variants could be read.');
      return rows;
    }
    async loadCatalog(status, fresh = false) {
      if (fresh || !this.catalog) status.textContent = 'Loading products…';
      if (this.catalogPromise) await this.catalogPromise;
      if (!fresh && this.catalog) return this.catalog;
      this.catalogPromise = (async () => {
        const variants = [],
          products = new Set(),
          ids = new Set();
        let pages = 1,
          total;
        for (let page = 1; page <= pages; page++) {
          if (!this.isConnected) throw new Error('Page closed.');
          const data = await this.dataPage(this.dataset.catalogUrl, page);
          if (
            data.kind !== 'products' ||
            !Array.isArray(data.products) ||
            data.products.length > 250 ||
            (page > 1 && (total !== data.total || pages !== data.pages))
          )
            throw new Error('Catalog changed while loading. Please try again.');
          pages = data.pages;
          total = data.total;
          for (const product of data.products) {
            if (
              !product ||
              !validId(product.id) ||
              products.has(product.id) ||
              !Number.isInteger(product.count) ||
              product.count < 1 ||
              product.count > 25000
            )
              throw new Error('Incomplete catalog. Please try again.');
            this.url(product.url, 'product');
            products.add(product.id);
            const rows = this.validateRows(product.rows);
            const complete = rows.length === product.count ? rows : await this.productRows(product.url);
            if (complete.length !== product.count) throw new Error('Product changed while loading.');
            for (const row of complete) {
              if (ids.has(row.id)) throw new Error('Repeated catalog page.');
              ids.add(row.id);
              variants.push(row);
            }
            if (variants.length > 100000)
              throw new Error('The catalog is too large for this tool. Contact the store for help.');
          }
        }
        if (products.size !== total) throw new Error('Not all products could be read.');
        this.catalog = variants;
        this.catalogLoadedAt = Date.now();
        return variants;
      })();
      try {
        return await this.catalogPromise;
      } finally {
        this.catalogPromise = null;
      }
    }
    async readCart() {
      const url = this.url(`${this.dataset.rootUrl.replace(/\/?$/, '/')}cart.js`);
      url.searchParams.set('_bulk', Date.now());
      const response = await this.request(url);
      if (!response.ok) throw new Error('The cart could not be checked. Please try again.');
      const cart = await response.json();
      if (!cart || !Array.isArray(cart.items) || cart.items.length > 10000)
        throw new Error('The cart could not be read.');
      const counts = new Map();
      for (const item of cart.items) {
        const id = String(item.variant_id);
        if (!validId(id) || !Number.isSafeInteger(item.quantity) || item.quantity < 0)
          throw new Error('Invalid cart quantities.');
        const total = (counts.get(id) || 0) + item.quantity;
        if (!Number.isSafeInteger(total)) throw new Error('Invalid cart quantities.');
        counts.set(id, total);
      }
      this.cartCounts = counts;
      return cart;
    }
    assertCartCapacity(cart, variantIds) {
      const capacity = core.cartCapacity(cart, variantIds);
      if (!capacity.fits) {
        const error = new Error(`Bulk-order limit: 480 cart lines. This leaves space for gifts below Shopify's 500-line limit. Your cart has ${capacity.current} lines, with room for ${capacity.remaining} more. This addition needs up to ${capacity.additional} new lines. Reduce the selection or check out your current cart first.`);
        error.code = 'BULK_CART_LINE_LIMIT';
        throw error;
      }
    }
    async runSearch() {
      if (this.busy || this.locked) return;
      const term = core.normalize(this.search.value);
      this.limit = 20;
      if (!term) {
        this.searchSequence = (this.searchSequence || 0) + 1;
        this.renderResults();
        return;
      }
      const sequence = (this.searchSequence = (this.searchSequence || 0) + 1);
      this.activeSearches = (this.activeSearches || 0) + 1;
      this.updateWorkingState();
      this.searchStatus.hidden = false;
      this.searchStatus.classList.add('is-loading');
      this.searchStatus.textContent = 'Loading products…';
      try {
        await this.loadCatalog(this.searchStatus);
        if (sequence !== this.searchSequence || this.busy || this.locked || !this.isConnected) return;
        await this.readCart();
        if (sequence === this.searchSequence && !this.busy && !this.locked && this.isConnected)
          this.renderResults();
      } catch (error) {
        if (sequence === this.searchSequence) this.searchStatus.textContent = error.message;
      } finally {
        this.activeSearches--;
        this.updateWorkingState();
        if (sequence === this.searchSequence) this.searchStatus.classList.remove('is-loading');
      }
    }
    renderResults() {
      const results = this.querySelector('[data-quick-results]');
      results.replaceChildren();
      const term = core.normalize(this.search.value);
      this.more.hidden = true;
      if (!term) {
        this.searchStatus.textContent = '';
        this.searchStatus.hidden = true;
        this.searchStatus.classList.remove('is-loading');
        return;
      }
      if (!this.catalog) return;
      this.searchStatus.hidden = false;
      const exact = [],
        others = [];
      for (const variant of this.catalog) {
        const sku = core.normalize(variant.sku),
          barcode = core.normalize(variant.barcode);
        if (sku === term || barcode === term) exact.push(variant);
        else if (sku.includes(term) || barcode.includes(term) || core.normalize(variant.name).includes(term))
          others.push(variant);
      }
      const matches = [...exact, ...others];
      this.searchStatus.textContent = matches.length
        ? `Showing ${Math.min(this.limit, matches.length)} of ${matches.length} matching items.`
        : 'No matching products. Try a full SKU, barcode, or product name.';
      for (const variant of matches.slice(0, this.limit)) {
        const row = element('div', 'bulk-order__row');
        const img = element('img', 'bulk-order__image');
        img.alt = '';
        img.width = 60;
        img.height = 70;
        img.loading = 'lazy';
        if (variant.image) {
          try {
            const imageURL = new URL(variant.image, location.origin);
            if (
              !imageURL.username &&
              !imageURL.password &&
              ((imageURL.origin === location.origin && imageURL.protocol === location.protocol) ||
                (imageURL.protocol === 'https:' && imageURL.hostname === 'cdn.shopify.com'))
            ) {
              img.src = imageURL.href;
              img.referrerPolicy = 'no-referrer';
            } else img.style.visibility = 'hidden';
          } catch (_error) {
            img.style.visibility = 'hidden';
          }
        } else img.style.visibility = 'hidden';
        const details = element('div');
        details.append(
          element('p', 'bulk-order__product-name', variant.name),
          element(
            'p',
            'bulk-order__codes',
            [
              variant.sku ? `SKU ${variant.sku}` : 'No SKU',
              variant.barcode ? `Barcode ${variant.barcode}` : '',
            ]
              .filter(Boolean)
              .join(' · '),
          ),
        );
        const inCart = this.cartCounts.get(variant.id) || 0;
        const remaining = variant.stock === null ? null : Math.max(0, variant.stock - inCart);
        const stockLabel =
          !variant.available || (variant.stock !== null && variant.stock <= 0)
            ? 'Out of stock'
            : remaining !== null && remaining < 6
              ? `${remaining} available to add`
              : 'In stock';
        details.append(
          element('p', 'bulk-order__stock', `${stockLabel}${inCart ? ` · ${inCart} in cart` : ''}`),
        );
        const actions = element('div', 'bulk-order__row-actions');
        for (const quantity of [6, 1]) {
          const button = element(
            'button',
            quantity === 6 ? 'button' : 'button button--secondary',
            `Add ${quantity}`,
          );
          button.type = 'button';
          const issue = core.stockIssue(variant, quantity, inCart);
          button.disabled = !!issue || this.busy || this.locked;
          button.title = issue || `Add ${quantity} ${variant.name}`;
          button.setAttribute('aria-label', `Add ${quantity}: ${variant.name}${issue ? ` — ${issue}` : ''}`);
          button.addEventListener('click', () => this.quickAdd(variant, quantity));
          actions.append(button);
        }
        row.append(img, details, element('div', 'bulk-order__price', variant.price), actions);
        results.append(row);
      }
      this.more.hidden = matches.length <= this.limit;
    }
    setBusy(busy) {
      this.busy = busy;
      this.file.disabled = busy || this.locked;
      this.dropzone.disabled = busy || this.locked;
      this.dropzone.classList.remove('is-dragging');
      this.search.disabled = busy || this.locked;
      this.querySelector('[data-search-button]').disabled = busy || this.locked;
      this.addCSV.disabled = busy || this.locked;
      this.setAttribute('aria-busy', String(busy));
      this.renderResults();
      this.updateWorkingState();
      this.updateReports();
    }
    reviewFiles(files) {
      if (this.busy || this.locked || !files.length) return;
      if (files.length !== 1) {
        this.rows = [];
        this.renderPreview();
        this.csvStatus.hidden = false;
        this.csvStatus.textContent = 'Please choose one CSV file at a time.';
        this.file.value = '';
        return;
      }
      this.reviewFile(files[0]);
    }
    async reviewFile(file) {
      if (this.busy || this.locked || !file) return;
      this.rows = [];
      this.renderPreview();
      this.setBusy(true);
      this.csvStatus.classList.add('is-loading');
      this.csvStatus.hidden = false;
      this.csvStatus.textContent = 'Reading CSV…';
      try {
        if (!/\.csv$/i.test(file.name)) throw new Error('Please choose a .csv file.');
        if (file.size > core.limits.bytes) throw new Error('Please use a CSV no larger than 1 MB.');
        const parsed = core.parseCSV(await file.text());
        await this.loadCatalog(this.csvStatus, !this.catalogLoadedAt || Date.now() - this.catalogLoadedAt > 60000);
        const cart = await this.readCart();
        const rows = core.matchRows(parsed, this.catalog, this.cartCounts);
        this.assertCartCapacity(cart, rows.filter((row) => row.state === 'ready').map((row) => row.variant.id));
        this.rows = rows;
        this.csvStatus.textContent = `${file.name}: ${this.rows.filter((row) => row.state === 'ready').length} items ready; ${this.rows.filter((row) => row.state === 'skipped').length} need attention.`;
        this.renderPreview();
      } catch (error) {
        this.csvStatus.textContent = error.message;
      } finally {
        this.csvStatus.classList.remove('is-loading');
        this.setBusy(false);
        this.file.value = '';
      }
    }
    renderPreview() {
      this.updateReports();
      const preview = this.querySelector('[data-csv-preview]');
      preview.replaceChildren();
      this.addCSV.hidden = !this.rows.some((row) => row.state === 'ready');
      if (!this.rows.length) return;
      const table = element('table');
      const caption = element('caption', 'visually-hidden', 'CSV order review');
      const head = element('thead'),
        heading = element('tr');
      for (const label of ['Code / product', 'Quantity', 'Status']) {
        const th = element('th', '', label);
        th.scope = 'col';
        heading.append(th);
      }
      head.append(heading);
      const body = element('tbody');
      for (const row of this.rows) {
        const tr = element('tr');
        const name = element('td', '', row.codes.join(' / ') || '(missing code)');
        if (row.variant) name.append(element('div', '', row.variant.name));
        name.append(
          element(
            'div',
            'bulk-order__fine-print',
            `CSV row${row.lines.length > 1 ? 's' : ''} ${row.lines.join(', ')}`,
          ),
        );
        const status =
          row.issue ||
          (row.state === 'ready' ? 'Ready' : row.state === 'pending' ? 'Checking stock…' : row.state);
        row.statusCell = element('td', '', status);
        tr.append(
          name,
          element('td', '', Number.isSafeInteger(row.quantity) ? String(row.quantity) : '—'),
          row.statusCell,
        );
        body.append(tr);
      }
      table.append(caption, head, body);
      preview.append(table);
    }
    async addItem(variant, quantity) {
      if (this.locked || !this.isConnected)
        throw new Error('Ordering is unavailable. Check your cart before trying again.');
      if (!validId(variant?.id) || !core.validQuantity(quantity)) throw new Error('Invalid cart addition.');
      return this.withCartLock(() => this.addCheckedItem(variant, quantity));
    }
    async withCartLock(callback) {
      const add = async () => {
        if (cartMutationBusy) throw new Error('Another bulk order is updating the cart. Please wait.');
        cartMutationBusy = true;
        try {
          return await callback();
        } finally {
          cartMutationBusy = false;
        }
      };
      if (navigator.locks?.request)
        return navigator.locks.request('retailer-bulk-cart', { ifAvailable: true }, (lock) => {
          if (!lock) throw new Error('Another bulk-order tab is updating the cart. Please wait.');
          return add();
        });
      return add();
    }
    async addCheckedItem(variant, quantity) {
      // The catalog is only a preview. Recheck the variant and existing cart for every addition.
      const freshRows = await this.productRows(variant.url);
      const fresh = freshRows.find((row) => row.id === variant.id);
      if (!fresh) return { added: 0, message: 'Product is no longer available' };
      if (this.catalog) {
        const index = this.catalog.findIndex((row) => row.id === fresh.id);
        if (index >= 0) this.catalog[index] = fresh;
      }
      const cart = await this.readCart();
      const before = this.cartCounts.get(fresh.id) || 0,
        issue = core.stockIssue(fresh, quantity, before);
      if (issue) return { added: 0, message: issue };
      this.assertCartCapacity(cart, [fresh.id]);
      if (!this.isConnected) throw new Error('Page closed before adding.');
      let response, data, failure;
      try {
        response = await this.request(this.url(`${this.dataset.rootUrl.replace(/\/?$/, '/')}cart/add.js`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: fresh.id, quantity }] }),
        });
        data = await response.json();
      } catch (error) {
        failure = error;
      }
      // Even a 422 or a dropped response can leave items in the cart. Never retry this POST.
      try {
        await this.readCart();
      } catch (error) {
        this.locked = true;
        throw new Error(
          'Cart confirmation failed. Ordering paused. Open the cart and verify it before reloading this page. Do not upload the same order again without checking.',
        );
      }
      const added = Math.max(0, (this.cartCounts.get(fresh.id) || 0) - before);
      if (failure || !response || response.status >= 500) {
        this.locked = true;
        return {
          added,
          uncertain: true,
          message: `Connection interrupted; ${added} more currently in cart. Ordering paused — check the cart before reloading.`,
        };
      }
      if (added === quantity) return { added, message: `Added ${added}` };
      if (added > 0)
        return { added, message: `Added ${added} of ${quantity}; stock or cart changed. Check your cart.` };
      return {
        added: 0,
        message:
          typeof data?.description === 'string'
            ? data.description
            : 'Not added — stock or quantity rules changed',
      };
    }
    async refreshCartDisplay() {
      try {
        const url = this.url(location.pathname);
        url.searchParams.set('sections', 'cart-icon-bubble,cart-drawer');
        url.searchParams.set('_bulk', Date.now());
        const response = await this.request(url);
        if (!response.ok) throw new Error('Cart display unavailable');
        const sections = await response.json();
        const parser = new DOMParser();
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && sections['cart-icon-bubble']) {
          const source = parser
            .parseFromString(sections['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section');
          if (source) bubble.innerHTML = source.innerHTML;
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer && sections['cart-drawer']) {
          const target = drawer.querySelector('#CartDrawer'),
            source = parser
              .parseFromString(sections['cart-drawer'], 'text/html')
              .querySelector('#CartDrawer');
          if (target && source) {
            target.innerHTML = source.innerHTML;
            drawer.classList.toggle(
              'is-empty',
              [...this.cartCounts.values()].every((count) => count === 0),
            );
            drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
          }
        }
      } catch (error) {
        this.orderStatus.textContent += ' Use View cart to see the latest cart contents.';
      }
    }
    async quickAdd(variant, quantity) {
      if (this.busy || this.locked) return;
      this.setBusy(true);
      this.orderStatus.textContent = `Checking ${variant.name}…`;
      try {
        const result = await this.addItem(variant, quantity);
        this.orderStatus.textContent = `${variant.name}: ${result.message}.`;
      } catch (error) {
        this.orderStatus.textContent = error.message;
      } finally {
        await this.refreshCartDisplay();
        this.setBusy(false);
      }
    }
    async addCheckedBatch(rows) {
      // One cart snapshot for the whole batch; Shopify enforces stock again on POST.
      const cart = await this.readCart();
      const variants = new Map(this.catalog.map((variant) => [variant.id, variant]));
      const results = new Map(), send = [];
      for (const row of rows) {
        const fresh = variants.get(row.variant.id);
        const issue = fresh
          ? core.stockIssue(fresh, row.quantity, this.cartCounts.get(fresh.id) || 0)
          : 'Product is no longer available';
        if (issue) results.set(row, { added: 0, message: issue });
        else {
          row.variant = fresh;
          send.push(row);
        }
      }
      this.assertCartCapacity(cart, send.map((row) => row.variant.id));
      if (!send.length) return results;
      if (this.stopRequested || !this.isConnected) return new Map();
      const before = new Map(this.cartCounts);
      for (const row of send) {
        row.state = 'pending';
        row.statusCell.textContent = 'Adding batch…';
      }
      let response, data, failure;
      try {
        response = await this.request(this.url(`${this.dataset.rootUrl.replace(/\/?$/, '/')}cart/add.js`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: send.map((row) => ({ id: row.variant.id, quantity: row.quantity })) }),
        });
        data = await response.json();
      } catch (error) {
        failure = error;
      }
      // Never replay a batch, even after a 422 or timeout: Shopify may have added items.
      try {
        await this.readCart();
      } catch (error) {
        this.locked = true;
        throw new Error('Cart confirmation failed. Ordering paused. Check your cart before reloading or uploading again.');
      }
      const deltas = send.map((row) => (this.cartCounts.get(row.variant.id) || 0) - (before.get(row.variant.id) || 0));
      const uncertain = !!failure || !response || response.status >= 500 ||
        deltas.some((delta, index) => delta < 0 || delta > send[index].quantity);
      if (uncertain) this.locked = true;
      // Throttling/authorization errors stop subsequent batches without replaying this one.
      if (response && !response.ok && response.status !== 422) this.stopRequested = true;
      send.forEach((row, index) => {
        const added = Math.max(0, deltas[index]);
        const message = uncertain
          ? `Unconfirmed result; ${added} more currently in cart. Ordering paused — check your cart.`
          : added === row.quantity ? `Added ${added}`
          : added > 0 ? `Added ${added} of ${row.quantity}; stock or cart changed. Check your cart.`
          : `Not added${typeof data?.description === 'string' ? ` — batch response: ${data.description}` : ' — stock or quantity rules changed'}`;
        results.set(row, { added, uncertain, message });
      });
      return results;
    }
    async addUpload() {
      if (this.busy || this.locked) return;
      this.setBusy(true);
      this.stopRequested = false;
      this.stopButton.hidden = false;
      this.stopButton.disabled = false;
      let units = 0,
        processed = 0;
      const pending = this.rows.filter((row) => row.state === 'ready');
      // Hold the existing cross-tab lock through stock refresh, writes and confirmations.
      try {
        await this.withCartLock(async () => {
          try {
            const cart = await this.readCart();
            this.assertCartCapacity(cart, pending.map((row) => row.variant.id));
          } catch (error) {
            this.csvStatus.textContent = `${error.message} Nothing was added from this upload in this run.`;
            this.orderStatus.textContent = 'Upload not started. Check your cart, then try again.';
            return;
          }
          let capacityMessage = '';
          for (let offset = 0; offset < pending.length; offset += core.limits.batchSize) {
            if (this.stopRequested || this.locked || !this.isConnected) break;
            const batch = pending.slice(offset, offset + core.limits.batchSize);
            try {
              await this.loadCatalog(this.csvStatus, !this.catalogLoadedAt || Date.now() - this.catalogLoadedAt > 60000);
              if (this.stopRequested || !this.isConnected) break;
              this.csvStatus.textContent = `Adding items ${offset + 1}–${offset + batch.length} of ${pending.length}…`;
              const results = await this.addCheckedBatch(batch);
              for (const [row, result] of results) {
                row.addedQuantity = result.added;
                row.state = result.uncertain ? 'uncertain' : result.added ? 'added' : 'skipped';
                row.issue = result.message;
                row.statusCell.textContent = row.issue;
                units += result.added;
                processed++;
              }
            } catch (error) {
              for (const row of batch) {
                // Reads that fail before POST leave rows resumable. Sent rows are never replayed.
                if (row.state === 'pending') {
                  row.state = 'uncertain';
                  processed++;
                }
                row.issue = error.message;
                row.statusCell.textContent = row.issue;
              }
              capacityMessage = error.message;
              this.stopRequested = true;
            }
          }
          const remaining = this.rows.filter((row) => row.state === 'ready').length;
          this.csvStatus.textContent = `${capacityMessage ? capacityMessage + ' ' : ''}${units} units added in this run. ${processed} items processed; ${remaining} remaining. See each row for its result.`;
          this.orderStatus.textContent = this.locked
            ? 'Ordering paused because a cart result is uncertain. Check View cart before reloading or uploading again.'
            : remaining
              ? 'Upload paused. Add available items resumes only the remaining rows.'
              : 'Upload finished. Review the results and your cart.';
        });
      } catch (error) {
        this.csvStatus.textContent = error.message;
        this.orderStatus.textContent = 'Upload paused. Check the message before continuing.';
      } finally {
        this.stopButton.hidden = true;
        await this.refreshCartDisplay();
        this.setBusy(false);
        this.renderPreview();
      }
    }
  }
  customElements.define('retailer-bulk-order', RetailerBulkOrder);
})();
